import { useEffect, useState } from 'react';
import { LG_BREAKPOINT_QUERY } from '../utils/breakpoints';

const STORAGE_KEY = 'admin-sidebar-mode';

const initialCollapsed = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'collapsed') return true;
    if (saved === 'expanded') return false;
  } catch {
    // ignore storage failures
  }
  // Viewport-aware default: collapsed below `lg`, expanded otherwise.
  if (window.matchMedia) {
    return !window.matchMedia(LG_BREAKPOINT_QUERY).matches;
  }
  return false;
};

/**
 * Persisted, viewport-aware admin-sidebar collapse state.
 * Shared by `AdminLayout`, `AuthenticatedLayout`, and any page that conditionally
 * renders the `AdminSidebar` so all callers stay in sync (one toggle changes
 * every layout).
 *
 * `sidebarPad` is the left-padding class for the main content wrapper —
 * always matches the sidebar's reserved inline width so content never sits
 * underneath the slim icon strip.
 *
 * Sync caveat: the `storage` event only fires in *other* tabs, so two layouts
 * that mount simultaneously in the same tab don't share state through this
 * mechanism. In practice this is fine — React Router unmounts the previous
 * layout before mounting the next, so only one consumer of this hook is
 * mounted per route. If two layouts ever needed to coexist (e.g. a side
 * sheet that wraps a sub-route in its own layout), promote this to a Zustand
 * store or a `BroadcastChannel` so toggles propagate within the tab too.
 */
export function useAdminSidebarState(): {
  collapsed: boolean;
  toggle: () => void;
  sidebarPad: 'lg:pl-16' | 'lg:pl-64';
} {
  const [collapsed, setCollapsed] = useState<boolean>(initialCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? 'collapsed' : 'expanded');
    } catch {
      // ignore storage failures
    }
  }, [collapsed]);

  // Cross-tab / cross-layout sync: if another layout toggles the sidebar,
  // pick up the change here too.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || e.newValue === null) return;
      setCollapsed(e.newValue === 'collapsed');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Padding only applies on lg+; on mobile the sidebar is an overlay drawer
  // and the content fills the viewport.
  return {
    collapsed,
    toggle: () => setCollapsed((c) => !c),
    sidebarPad: collapsed ? 'lg:pl-16' : 'lg:pl-64',
  };
}
